import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, PackageCheck } from "lucide-react";
import { api } from "@/lib/api";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];

export default function GoodsReceiptNotes() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [grns, setGrns] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [grnRes, poRes, whRes, itemRes] = await Promise.all([
        api.get("/procurement/grns"), api.get("/procurement/purchase-orders"), api.get("/inventory/warehouses"),
        api.get("/inventory/items").catch(() => ({ data: [] })),
      ]);
      setInventoryItems(itemRes.data);
      setGrns(grnRes.data);
      setPurchaseOrders(poRes.data);
      setWarehouses(whRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const poNumber = (id) => purchaseOrders.find((p) => p.id === id)?.number || id;

  const post = async (grn) => {
    setBusyId(grn.id);
    try {
      await api.post(`/procurement/grns/${grn.id}/post`);
      toast.success("GRN posted to GL (Inventory / GRIR)" + (grn.warehouse_id ? " and stock received" : ""));
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const addToStock = async (grn) => {
    setBusyId(grn.id);
    try {
      const { data } = await api.post(`/procurement/grns/${grn.id}/receive-stock`);
      if (data.received?.length) toast.success(`Added to stock: ${data.received.join(", ")}`);
      else if (!data.skipped?.length) toast.info("This GRN is already in stock — nothing to add");
      (data.skipped || []).forEach((m) => toast.warning(`Not added: ${m}`));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="grns-page">
      <PageHeader title="Goods Receipt Notes" description="Record goods received against an approved PO. Posting creates a Dr Inventory / Cr GRIR journal entry." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateGRNDialog companies={companies} purchaseOrders={purchaseOrders.filter((p) => p.status === "APPROVED")} grns={grns} warehouses={warehouses} inventoryItems={inventoryItems} onCreated={load} />}</div>} />
      {!loading && grns.length === 0 ? (
        <EmptyState title="No GRNs yet" description="Approve a Purchase Order first, then receive goods against it here." testId="grns-empty-state" />
      ) : (
        <div className="space-y-3">
          {pf.apply(grns, (r) => r.project_id).map((grn) => (
            <div key={grn.id} className="rounded-xl border border-border bg-card p-4" data-testid={`grn-card-${grn.number}`}>
              <div className="flex items-center justify-between">
                <div><p className="font-mono text-xs text-muted-foreground">{grn.number}</p><p className="text-sm text-foreground">Against {poNumber(grn.purchase_order_id)} · {grn.grn_date} · {grn.warehouse_id ? `into ${warehouses.find((w) => w.id === grn.warehouse_id)?.name || "warehouse"}` : "direct to site (no stock)"}</p></div>
                <div className="flex items-center gap-2">
                  <StatusPill value={grn.status} />
                  {canWrite && grn.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === grn.id} onClick={() => post(grn)} data-testid={`post-grn-button-${grn.number}`}>
                      <PackageCheck className="h-3.5 w-3.5" /> Post
                    </Button>
                  )}
                  {canWrite && grn.status === "POSTED" && grn.warehouse_id && (
                    <Button size="sm" variant="outline" disabled={busyId === grn.id} onClick={() => addToStock(grn)} title="Add this GRN's goods to warehouse stock (safe to repeat)" data-testid={`grn-add-stock-button-${grn.number}`}>
                      <PackageCheck className="h-3.5 w-3.5" /> Add to stock
                    </Button>
                  )}
                  <DocumentFlowButton docType="GRN" docId={grn.id} cancelEndpoint={grn.status === "POSTED" ? `/procurement/grns/${grn.id}/cancel` : undefined} onCancelled={load} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateGRNDialog({ companies, purchaseOrders, grns, warehouses, inventoryItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", purchase_order_id: "", warehouse_id: "", grn_date: "" });
  const [quantities, setQuantities] = useState({});
  const [lineItems, setLineItems] = useState({});
  const [saving, setSaving] = useState(false);
  const stockItems = (inventoryItems || []).filter((i) => i.category === "STOCK" && i.is_active !== false);
  const norm = (t) => String(t || "").toLowerCase().replace(/-/g, " ").split(/\s+/).filter(Boolean).join(" ");
  const guessItem = (poLine) => {
    if (poLine.item_id) return poLine.item_id;
    const d = norm(poLine.description);
    const exact = stockItems.filter((i) => norm(i.name) === d || norm(i.code) === d);
    if (exact.length === 1) return exact[0].id;
    const partial = stockItems.filter((i) => d.includes(norm(i.name)) || norm(i.name).includes(d));
    return partial.length === 1 ? partial[0].id : "";
  };
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const selectedPO = purchaseOrders.find((p) => p.id === form.purchase_order_id);

  // Quantity already received on each PO line (all GRNs except cancelled ones)
  const receivedSoFar = (poItemId) =>
    (grns || [])
      .filter((g) => g.status !== "CANCELLED")
      .flatMap((g) => g.items || [])
      .filter((i) => i.po_item_id === poItemId)
      .reduce((sum, i) => sum + Number(i.quantity_received || 0), 0);
  const pendingQty = (item) => Math.max(Number(item.quantity) - receivedSoFar(item.id), 0);

  const selectPO = (id) => {
    const po = purchaseOrders.find((p) => p.id === id);
    setForm({ ...form, purchase_order_id: id, company_id: po?.company_id || form.company_id });
    setQuantities(Object.fromEntries((po?.items || []).map((it) => [it.id, pendingQty(it) ? String(+pendingQty(it).toFixed(3)) : ""])));
    setLineItems(Object.fromEntries((po?.items || []).map((it) => [it.id, guessItem(it)])));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const items = Object.entries(quantities).filter(([, qty]) => qty).map(([po_item_id, quantity_received]) => ({ po_item_id, quantity_received, item_id: lineItems[po_item_id] || null }));
      await api.post("/procurement/grns", { ...form, warehouse_id: form.warehouse_id || null, items });
      toast.success("GRN created");
      setForm({ company_id: "", purchase_order_id: "", warehouse_id: "", grn_date: "" });
      setQuantities({});
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-grn-button"><Plus className="h-4 w-4" /> New GRN</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Goods Receipt Note</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-grn-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="grn-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="grn-date-input" value={form.grn_date} onChange={set("grn_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Purchase Order</Label>
              <Select required value={form.purchase_order_id} onValueChange={selectPO}>
                <SelectTrigger data-testid="grn-po-select" className="mt-1.5"><SelectValue placeholder="Select an approved PO" /></SelectTrigger>
                <SelectContent>{purchaseOrders.map((p) => <SelectItem key={p.id} value={p.id}>{p.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warehouse <span className="font-normal text-muted-foreground">(choose one to add to stock)</span></Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="grn-warehouse-select" className="mt-1.5"><SelectValue placeholder="Raise stock on posting" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {selectedPO && (
            <div className="space-y-2" data-testid="grn-po-items">
              <Label>Quantities received <span className="font-normal text-muted-foreground">(pre-filled with what is still pending — change to what actually arrived)</span></Label>
              {selectedPO.items.map((item) => (
                <div key={item.id} className="space-y-1.5 rounded-lg border border-border p-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-muted-foreground">{item.description} (ordered {Number(item.quantity)}, received so far {receivedSoFar(item.id)}, pending {pendingQty(item)})</span>
                    <Input type="number" step="0.001" className="w-28" value={quantities[item.id] || ""} onChange={(e) => setQuantities({ ...quantities, [item.id]: e.target.value })} data-testid={`grn-item-qty-input-${item.id}`} />
                  </div>
                  {form.warehouse_id && (
                    <div className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-[11px] text-muted-foreground">Inventory item</span>
                      <Select value={lineItems[item.id] || "none"} onValueChange={(v) => setLineItems({ ...lineItems, [item.id]: v === "none" ? "" : v })}>
                        <SelectTrigger className={`h-8 min-w-0 flex-1 text-xs ${lineItems[item.id] ? "" : "border-amber-500/60 text-amber-600 dark:text-amber-400"}`} data-testid={`grn-item-inventory-select-${item.id}`}>
                          <SelectValue placeholder="Choose the stock item" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not linked — won't be added to stock</SelectItem>
                          {stockItems.map((si) => <SelectItem key={si.id} value={si.id}>{si.code} — {si.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              {form.warehouse_id && stockItems.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">No STOCK items exist yet. Create them in Inventory → Items first, then come back.</p>
              )}
              {!form.warehouse_id && (
                <p className="text-xs text-muted-foreground">No warehouse chosen: goods are treated as delivered straight to site and are not added to stock.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || !selectedPO} data-testid="create-grn-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
